import {Stack} from "@src/CommonComponents/Stack/Stack";
import styles from "./purchaseRequests.module.less";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {useTranslation} from "react-i18next";
import cn from "classnames";
import {useNavigate} from "react-router-dom";
import {useMutation} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {apiClient} from "@src/httpClient/api.ts";
import {useRequestAction} from "../useRequestAction.ts"
import {getApiErrorMessage} from "@src/httpClient/utils/apiError.ts"
import {
    ExpenseRequest as ExpenseRequestType,
    ExpenseRequestRequest,
    CategoryEnum, RequestStatusEnum,
} from "@src/httpClient/__generated__/Api.ts";
import {queryClient} from "@src/QueryClient/queryClient.ts";
import {useParams} from "react-router";
import {useEntityQuery} from "@src/httpClient/utils/queryHelpers.ts";
import {backendUrl} from "@src/utils/backendUrl.ts";
import {Body_14_Coral_Empasis_14, Body_14_Coral_Primary_Regular_14} from "@UI/typography/generated/typography.tsx";
import {TextInput} from "@src/CommonComponents/Input/TextInput";
import {Textarea} from "@src/CommonComponents/Input/Textarea.tsx";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {AvatarWithInfo} from "@src/Pages/SettingsPage/MainMenu.tsx";
import {Checked} from "@UI/components/icons/Checked.tsx";
import {
    ScrollableFilterToggle
} from "@src/CommonComponents/FilterSelect/ScrollableFilterToggle/ScrollableFilterToggle.tsx";
import {useErrorStore} from "@src/Layers/ErrorLayer/ErrorStore.ts";
import {Trash} from "@UI/components/icons/Trash.tsx";
import {Xcross} from "@UI/components/icons/Xcross.tsx";
import {BackButtonRow} from "@src/CommonComponents/HeaderWithBackButton/HeaderWithBackButton.tsx";

// Mirror the backend's receipt rules (checked by extension and size only)
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_RECEIPTS = 5
const RECEIPT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'pdf']
const RECEIPT_ACCEPT = 'image/*,application/pdf'

const ReceiptThumb = ({name, src, onOpen, onRemove}: {
    name: string,
    src: string | undefined,
    onOpen?: () => void,
    onRemove?: () => void,
}) => (
    <div className={styles.previewItem}>
        {name.toLowerCase().endsWith('.pdf') ? (
            <button type="button" className={styles.fileTile} onClick={onOpen}>
                <span className={styles.fileTileLabel}>PDF</span>
            </button>
        ) : (
            <img src={src} alt="" className={styles.previewImage} onClick={onOpen}/>
        )}
        {onRemove && (
            <button type="button" className={styles.removeButton} onClick={onRemove}>
                <Xcross size={14}/>
            </button>
        )}
    </div>
)

const categoryLabels: Record<CategoryEnum, string> = {
    [CategoryEnum.Fodder]: 'Fodder',
    [CategoryEnum.Fuel]: 'Fuel',
    [CategoryEnum.Travel]: 'Travel',
    [CategoryEnum.Accommodation]: 'Accommodation',
    [CategoryEnum.Meals]: 'Meals',
    [CategoryEnum.Supplies]: 'Supplies',
    [CategoryEnum.Equipment]: 'Equipment',
    [CategoryEnum.Communication]: 'Communication',
    [CategoryEnum.Other]: 'Other',
}

const CategorySelector = ({value, onChange, disabled}: {
    value: CategoryEnum,
    onChange: (v: CategoryEnum) => void,
    disabled?: boolean,
}) => {
    return (
        <div className={styles.typeSelector}>
            {Object.values(CategoryEnum).map(cat => (
                <Button
                    key={cat}
                    type={value === cat ? ButtonType.secondary : ButtonType.third}
                    className={cn(styles.typeChip, {[styles.typeChipActive]: value === cat})}
                    onClick={() => !disabled && onChange(cat)}
                    disabled={disabled}
                >
                    <Gt>{categoryLabels[cat]}</Gt>
                </Button>
            ))}
        </div>
    )
}

interface FormState {
    expense_date: string
    amount: string
    description: string
}

const emptyForm: FormState = {
    expense_date: '',
    amount: '',
    description: '',
}

export const PurchaseRequest = () => {
    const {requestId} = useParams<{ requestId: string }>()
    const navigate = useNavigate()
    const employee = useCredentialsStore(x => x.employee)
    const {t} = useTranslation()
    const setAppError = useErrorStore(x => x.setAppError)

    const isViewMode = !!requestId
    const isManager = !!employee?.is_manager
    const {actionInFlight, runAction} = useRequestAction("expenseRequest", "/requests/?tab=purchase")
    const [rejectReason, setRejectReason] = useState('')
    const [category, setCategory] = useState<CategoryEnum>(CategoryEnum.Other)
    const [form, setForm] = useState<FormState>(emptyForm)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const fileInputRef = useRef<HTMLInputElement>(null)
    // Once the request is created, a failed receipt upload must not create
    // it again on retry: keep its id and the files already uploaded.
    const createdIdRef = useRef<number | null>(null)
    const uploadedRef = useRef(new Set<File>())
    const [isCreated, setIsCreated] = useState(false)

    // Load existing request
    const {data, isLoading} = useEntityQuery<ExpenseRequestType>(
        ["expenseRequest", requestId],
        async () => await apiClient.api.workflowExpenseRequestsRetrieve(Number(requestId))
    )

    const isOwnRequest = !isViewMode || (data?.employee === employee?.id)
    const isManagerReview = isViewMode && isManager && !isOwnRequest
    const receiptCount = data?.receipts?.length ?? 0

    // Populate form from loaded data
    useEffect(() => {
        if (!data) return
        setCategory(data.category ?? CategoryEnum.Other)
        setForm({
            expense_date: data.expense_date ?? '',
            amount: data.amount ?? '',
            description: data.description ?? '',
        })
    }, [data])

    const onChange = useCallback((field: keyof FormState, value: string) => {
        setForm(prev => ({...prev, [field]: value}))
        setErrors(prev => {
            const next = {...prev}
            delete next[field]
            return next
        })
    }, [])

    const validate = (): boolean => {
        const e: Record<string, string> = {}
        if (!form.expense_date) e.expense_date = 'Required'
        if (!form.amount) e.amount = 'Required'
        if (!form.description) e.description = 'Required'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    // Keeps the files that pass the backend's rules and fit into the free slots
    const acceptFiles = useCallback((files: File[], freeSlots: number): File[] => {
        const problems: string[] = []
        const valid = files.filter(f => {
            if (!RECEIPT_EXTENSIONS.includes(f.name.split('.').pop()?.toLowerCase() ?? '')) {
                problems.push(`${f.name}: ${t('Unsupported file type')}`)
                return false
            }
            if (f.size > MAX_FILE_SIZE) {
                problems.push(`${f.name}: ${t('File size exceeds 10MB')}`)
                return false
            }
            return true
        })
        if (valid.length > freeSlots) {
            problems.push(t('You can attach up to {{count}} receipts', {count: MAX_RECEIPTS}))
        }
        if (problems.length) setAppError({message: problems.join('\n'), showModal: true})
        return valid.slice(0, Math.max(freeSlots, 0))
    }, [t])

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = acceptFiles(Array.from(e.target.files || []), MAX_RECEIPTS - selectedFiles.length)
        setSelectedFiles(prev => [...prev, ...files])
        if (fileInputRef.current) fileInputRef.current.value = ''
    }, [acceptFiles, selectedFiles.length])

    const removeFile = useCallback((index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    }, [])

    const {mutate, isPending} = useMutation<number, AxiosError>({
        mutationKey: ["expenseRequest", "create"],
        mutationFn: async () => {
            if (createdIdRef.current === null) {
                const payload: ExpenseRequestRequest = {
                    employee: employee!.id,
                    expense_date: form.expense_date,
                    amount: form.amount,
                    category,
                    description: form.description,
                }
                const result = await apiClient.api.workflowExpenseRequestsCreate(payload)
                createdIdRef.current = result.data.id
                setIsCreated(true)
            }
            const requestId = createdIdRef.current
            for (const file of selectedFiles) {
                if (uploadedRef.current.has(file)) continue
                await apiClient.api.workflowExpenseRequestsReceiptsCreate(requestId, {image: file})
                uploadedRef.current.add(file)
            }
            return requestId
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["expenseRequest"]})
            queryClient.invalidateQueries({queryKey: ["requests-list"]})
            navigate("/requests/?tab=purchase")
        },
        onError: (error: AxiosError) => {
            setAppError({
                axiosError: error,
                message: getApiErrorMessage(error),
                showModal: true,
                retry: async () => mutate(),
            })
        },
    })

    const isFormValid = useMemo(() => {
        return !!(form.expense_date && form.amount && form.description)
    }, [form])

    const handleSubmit = useCallback(() => {
        if (!validate()) return
        mutate()
    }, [form, category])

    return (
        <Stack direction={"column"} gap={16} className={styles.subMenuPage}>
            <BackButtonRow title={"Back"} backLink={"/requests/?tab=purchase"} hideOnMobile/>
            <AvatarWithInfo employee={isViewMode && data ? {id: data.employee, first_name: data.employee_name} as any : employee}/>

            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                <ScrollableFilterToggle>
                    <CategorySelector
                        value={category}
                        onChange={setCategory}
                        disabled={isViewMode || isCreated}
                    />
                </ScrollableFilterToggle>
            </Stack>

            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Date</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="expense_date"
                        value={form.expense_date}
                        onChange={v => onChange('expense_date', v)}
                        type="date"
                        hasError={!!errors.expense_date}
                        disabled={isViewMode || isCreated}
                    />
                </Stack>

                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Amount</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="amount"
                        value={form.amount}
                        onChange={v => onChange('amount', v)}
                        type="number"
                        placeholder="0"
                        hasError={!!errors.amount}
                        disabled={isViewMode || isCreated}
                    />
                </Stack>
            </Stack>

            <Stack direction={"column"} className={styles.whiteBlock} gap={24}>
                <Body_14_Coral_Empasis_14>
                    <Gt>Description</Gt>
                </Body_14_Coral_Empasis_14>
                <Textarea
                    name="description"
                    initialValue={form.description}
                    onValueChange={v => onChange('description', v as string)}
                    disabled={isViewMode || isCreated}
                />
            </Stack>

            {!isViewMode && (
                <Stack direction={"column"} className={styles.whiteBlock} gap={12}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Receipts</Gt>
                    </Body_14_Coral_Empasis_14>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={RECEIPT_ACCEPT}
                        multiple
                        onChange={handleFileSelect}
                        className={styles.fileInput}
                    />
                    {selectedFiles.length < MAX_RECEIPTS && (
                        <Button
                            type={ButtonType.third}
                            className={styles.uploadButton}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Gt>Add photos or PDFs</Gt>
                        </Button>
                    )}
                    {selectedFiles.length > 0 && (
                        <div className={styles.previewGrid}>
                            {selectedFiles.map((file, i) => (
                                <ReceiptThumb
                                    key={i}
                                    name={file.name}
                                    src={URL.createObjectURL(file)}
                                    // An uploaded file is already attached to the created request
                                    onRemove={uploadedRef.current.has(file) ? undefined : () => removeFile(i)}
                                />
                            ))}
                        </div>
                    )}
                </Stack>
            )}

            {/* A filed request is not edited, its receipts included */}
            {isViewMode && receiptCount > 0 && (
                <Stack direction={"column"} className={styles.whiteBlock} gap={12}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Receipts</Gt>
                    </Body_14_Coral_Empasis_14>
                    <div className={styles.previewGrid}>
                        {data!.receipts.map(receipt => (
                            <ReceiptThumb
                                key={receipt.id}
                                // Signed S3 URLs carry a query string; the extension is on the path
                                name={receipt.url ? new URL(receipt.url, window.location.origin).pathname : ''}
                                src={receipt.url ? backendUrl(receipt.url) : undefined}
                                onOpen={() => receipt.url && window.open(backendUrl(receipt.url), '_blank')}
                            />
                        ))}
                    </div>
                </Stack>
            )}

            {isViewMode && data?.status === RequestStatusEnum.Rejected && (
                <Stack direction={"column"} className={styles.whiteBlock} gap={12}>
                    <Stack direction={"column"} gap={6}>
                        <Body_14_Coral_Empasis_14>
                            <Gt>Rejected by</Gt>
                        </Body_14_Coral_Empasis_14>
                        <Body_14_Coral_Primary_Regular_14>
                            {data.reviewed_by_name}
                        </Body_14_Coral_Primary_Regular_14>
                    </Stack>
                    {data.reject_reason && (
                        <Stack direction={"column"} gap={6}>
                            <Body_14_Coral_Empasis_14>
                                <Gt>Rejection reason</Gt>
                            </Body_14_Coral_Empasis_14>
                            <Body_14_Coral_Primary_Regular_14>
                                {data.reject_reason}
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>
                    )}
                </Stack>
            )}

            {!isViewMode && (
                <Button
                    icon={<Checked/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_GREEN}
                    className={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={isPending || !employee?.id || !isFormValid}
                >
                    <Gt>Submit application</Gt>
                </Button>
            )}

            {isViewMode && data?.status === RequestStatusEnum.Pending && isOwnRequest && (
                <Button
                    icon={<Trash/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_FIRE}
                    className={styles.submitButton}
                    disabled={actionInFlight}
                    onClick={() => runAction(() => apiClient.api.workflowExpenseRequestsCancelCreate(Number(requestId)))}
                >
                    <Gt>Cancel request</Gt>
                </Button>
            )}

            {isManagerReview && data?.status === RequestStatusEnum.Pending && (
                <>
                    <Textarea
                        name="reject_reason"
                        initialValue={rejectReason}
                        onValueChange={v => setRejectReason(v as string)}
                        placeholder={t('Rejection reason')}
                    />
                    <div className={styles.actionButtons}>
                        <Button
                            icon={<Checked/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_GREEN}
                            className={styles.actionButton}
                            disabled={actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowExpenseRequestsApproveCreate(Number(requestId)))}
                        >
                            <Gt>Approve</Gt>
                        </Button>
                        <Button
                            icon={<Xcross/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_FIRE}
                            className={styles.actionButton}
                            disabled={!rejectReason || actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowExpenseRequestsRejectCreate(Number(requestId), {reason: rejectReason}))}
                        >
                            <Gt>Reject</Gt>
                        </Button>
                    </div>
                </>
            )}
        </Stack>
    )
}
